/**
 * Memory controller: the session's plug-and-play memory subsystem — the read-only OKF retrieval
 * provider, the bounded prompt-evidence surfacing pilot, cross-session recall effectiveness, and the
 * live {@link MemoryManager} (bundled file-store + transcript-recall providers plus any extension
 * contributions).
 *
 * Extracted verbatim from agent-session.ts (god-file decomposition). Owns the lazily-built OKF
 * provider, the latest retrieval/prompt-inclusion reports, the recreated-on-reload MemoryManager, the
 * recall {@link EffectivenessTracker}, and the extension-contributed pending providers. Everything
 * else it needs — settings, the current turn index, agent/workspace dirs, the session id, the
 * child-session flag, and the tool-registry refresh — is reached through narrow deps accessors rather
 * than the whole AgentSession.
 *
 * Context-transform boundary (deliberate): {@link runMemoryRetrieval} and
 * {@link maybeAppendMemoryEvidenceBlock} are invoked from the session's context transform as one-line
 * delegations. This controller deliberately imports no compaction/context-pipeline internals — it only
 * ever reads settings and builds the retrieval report + the bounded evidence block, so the transform
 * stays the single owner of the pass ordering.
 */

import type { AgentMessage, CustomMessage } from "@caupulican/pi-agent-core";
import { configFile, okfMemoryDir } from "./agent-paths.ts";
import { collectCurrentWorkMemory } from "./context/current-work-memory.ts";
import { createFileStoreMemoryProvider } from "./context/file-store-memory-provider.ts";
import { createLocalGraphMemoryProvider } from "./context/local-graph-memory-provider.ts";
import { shouldQueryLongTermMemory } from "./context/long-term-memory-trigger.ts";
import {
	defaultMemoryPromptInclusionReport,
	type MemoryPromptInclusionReport,
	type MemoryRetrievalDiagnostics,
	sanitizeMemoryRetrievalReportForDiagnostics,
} from "./context/memory-diagnostics.ts";
import { type MemoryPromptBudget, resolveMemoryPromptBudget } from "./context/memory-prompt-budget.ts";
import {
	type MemoryProvider as ContextMemoryProvider,
	DEFAULT_EXTERNAL_MEMORY_EGRESS_POLICY,
	DEFAULT_LOCAL_MEMORY_EGRESS_POLICY,
} from "./context/memory-provider-contract.ts";
import { type MemoryRetrievalReport, retrieveMemoryForContext } from "./context/memory-retrieval.ts";
import { composeTieredMemoryPromptBlock, type MemoryTierCandidate } from "./context/memory-tier-composer.ts";
import { createOkfMemoryProvider, loadOkfMemoryBundle } from "./context/okf-memory-provider.ts";
import type { GoalState } from "./goals/goal-state.ts";
import { EffectivenessTracker } from "./memory/effectiveness-tracker.ts";
import { MemoryManager } from "./memory/memory-manager.ts";
import type { MemoryProvider } from "./memory/memory-provider.ts";
import {
	FILE_STORE_MEMORY_SYSTEM_NOTE,
	FileStoreProvider,
	type StructuredReflectionApplyResult,
	type StructuredReflectionRollback,
	type StructuredReflectionWrite,
} from "./memory/providers/file-store.ts";
import { TranscriptRecallProvider } from "./memory/providers/transcript-recall.ts";
import { wrapUntrustedText } from "./security/untrusted-boundary.ts";
import { getDirectoryResourceProfileInfo, type SettingsManager } from "./settings-manager.ts";

/**
 * Text of the most recent user message, or "" if there is none (e.g. goal-continuation
 * turns with no new user input). An empty query is still valid: standing user preferences may
 * surface from the file-store retrieval fallback when the static memory prompt cannot fit,
 * while long-term providers remain gated by shouldQueryLongTermMemory().
 */
function latestUserMessageText(messages: AgentMessage[]): string {
	for (let index = messages.length - 1; index >= 0; index--) {
		const message = messages[index];
		if (message.role !== "user") continue;
		if (typeof message.content === "string") return message.content;
		const parts: string[] = [];
		for (const part of message.content) {
			if (part.type === "text") parts.push(part.text);
		}
		return parts.join("\n");
	}
	return "";
}

function emptyMemoryRetrievalReport(maxResults: number): MemoryRetrievalReport {
	return { request: { query: "", maxResults }, providerReports: [], results: [], contextItems: [] };
}

const ENABLED_EXTERNAL_MEMORY_EGRESS_POLICY = {
	...DEFAULT_EXTERNAL_MEMORY_EGRESS_POLICY,
	enabled: true,
	allowedScopes: DEFAULT_LOCAL_MEMORY_EGRESS_POLICY.allowedScopes,
	allowExternalEgress: true,
} as const;

const MAX_PRE_COMPRESS_MEMORY_CHARS = 4_000;

function boundPreCompressMemory(text: string): string {
	const trimmed = text.trim();
	if (trimmed.length <= MAX_PRE_COMPRESS_MEMORY_CHARS) return trimmed;
	const suffix = "\n...[memory handoff truncated]";
	return `${trimmed.slice(0, MAX_PRE_COMPRESS_MEMORY_CHARS - suffix.length).trimEnd()}${suffix}`;
}

export interface MemoryControllerDeps {
	/** Memory-retrieval + prompt-inclusion settings (default-on gates for retrieval and surfacing). */
	getSettingsManager(): SettingsManager;
	/** Current turn index, stamped into a retrieval request's `createdAtTurn`. */
	getTurnIndex(): number;
	/** Agent root — the durable OKF memory docs live under `<agentDir>/okf-memory`. */
	getAgentDir(): string;
	/** Workspace root, passed to provider initialization. */
	getCwd(): string;
	/** This session's id, passed to provider initialization. */
	getSessionId(): string;
	/** Child sessions gate durable memory writes; passed to provider initialization. */
	isChildSession(): boolean;
	/** Re-derive the tool registry after (re)init so the newly-surfaced memory tools take effect. */
	refreshToolRegistry(): void;
	/** Active model context window, used to cap prompt-visible memory. */
	getContextWindow(): number | undefined;
	/** Latest active goal state, used for short-term current-work memory. */
	getGoalState(): GoalState | undefined;
}

/** Extension-contributed memory state staged across an atomic runtime reload. */
export interface MemoryControllerReloadSnapshot {
	pendingMemoryProviders: MemoryProvider[];
	pendingContextMemoryProviders: ContextMemoryProvider[];
	memoryOkfProvider: ContextMemoryProvider | undefined;
	fileStoreMemoryProvider: ContextMemoryProvider | undefined;
}

export class MemoryController {
	private _memoryOkfProvider: ContextMemoryProvider | undefined = undefined;
	private _fileStoreMemoryProvider: ContextMemoryProvider | undefined = undefined;
	private _localGraphProvider: ContextMemoryProvider | undefined = undefined;
	private _localGraphResolved = false;
	private _latestMemoryRetrievalReport: MemoryRetrievalReport | undefined = undefined;
	private _latestMemoryPromptInclusionReport: MemoryPromptInclusionReport | undefined = undefined;
	/** Plug-and-play memory subsystem. Recreated on each (re)initialize so reload is safe. */
	private _memoryManager: MemoryManager = new MemoryManager();
	/** Active generation's single durable file/OKF writer, also used by parent-owned reflection. */
	private _fileStoreWriter: FileStoreProvider | undefined;
	/** R4: tracks whether injected recall is actually used, to adapt the recall gate. */
	private readonly _effectivenessTracker = new EffectivenessTracker();
	/** Memory providers registered by extensions via pi.registerMemoryProvider, applied on (re)init. */
	private _pendingMemoryProviders: MemoryProvider[] = [];
	/** Context-memory providers registered by extensions via pi.registerContextMemoryProvider. */
	private _pendingContextMemoryProviders: ContextMemoryProvider[] = [];
	/** Serializes provider write hooks without delaying the foreground turn. */
	private _lifecycleTail: Promise<void> = Promise.resolve();
	private _shutdownPromise: Promise<void> | undefined;

	private readonly deps: MemoryControllerDeps;

	constructor(deps: MemoryControllerDeps) {
		this.deps = deps;
	}

	/** The live memory manager. Callers reach prefetch / tool-definitions / markers / shutdown through it. */
	getMemoryManager(): MemoryManager {
		return this._memoryManager;
	}

	/** Queue one completed turn for provider-owned durable synchronization. Raw tool output is excluded by the caller. */
	scheduleTurnSync(userText: string, assistantText: string): void {
		if (!userText.trim() && !assistantText.trim()) return;
		const manager = this._memoryManager;
		this._lifecycleTail = this._lifecycleTail
			.then(() => manager.syncTurn(userText, assistantText))
			.catch((error) => {
				console.error(
					"Memory turn synchronization failed:",
					error instanceof Error ? error.message : String(error),
				);
			});
	}

	/** Wait for prior turn writes, then collect one bounded provider handoff for the whole compaction run. */
	async onPreCompress(): Promise<string> {
		await this._lifecycleTail;
		return boundPreCompressMemory(await this._memoryManager.onPreCompress());
	}

	/** Flush write-side lifecycle hooks before releasing provider resources. Idempotent per session. */
	shutdown(): Promise<void> {
		this._shutdownPromise ??= (async () => {
			this._fileStoreWriter = undefined;
			await this._lifecycleTail;
			await this._memoryManager.onSessionEnd();
			await this._memoryManager.shutdownAll();
		})();
		return this._shutdownPromise;
	}

	/**
	 * Fixed path for this slice's local Pi OKF memory documents, shared across sessions
	 * under this agentDir (not session-scoped, unlike tool-artifacts/context-gc, since OKF
	 * memory represents durable cross-session knowledge, not a per-session capture). Not
	 * yet user-configurable -- see the memory-retrieval settings doc comment.
	 */
	private _memoryOkfDir(): string {
		return okfMemoryDir(this.deps.getAgentDir());
	}

	/**
	 * Session-scoped, read-only local OKF memory provider. Lazily created ONLY when memory
	 * retrieval is enabled (see `runMemoryRetrieval`) -- never force-created, so a session
	 * with the setting off never touches `_memoryOkfDir()` at all (no directory access, no
	 * creation; `createOkfMemoryProvider` itself never writes/mkdirs either way).
	 */
	private _getMemoryOkfProvider(): ContextMemoryProvider {
		const project = getDirectoryResourceProfileInfo(this.deps.getCwd(), this.deps.getAgentDir());
		this._memoryOkfProvider ??= createOkfMemoryProvider({
			rootDir: this._memoryOkfDir(),
			projectId: project.hash,
			projectRoot: project.root,
		});
		return this._memoryOkfProvider;
	}

	private _getFileStoreMemoryProvider(): ContextMemoryProvider {
		this._fileStoreMemoryProvider ??= createFileStoreMemoryProvider({
			memoryFilePath: configFile(this.deps.getAgentDir(), "MEMORY.md"),
			userFilePath: configFile(this.deps.getAgentDir(), "USER.md"),
		});
		return this._fileStoreMemoryProvider;
	}

	private _getLocalGraphProvider(): ContextMemoryProvider | undefined {
		if (!this._localGraphResolved) {
			this._localGraphProvider = createLocalGraphMemoryProvider({
				cwd: this.deps.getCwd(),
				agentDir: this.deps.getAgentDir(),
			});
			this._localGraphResolved = true;
		}
		return this._localGraphProvider;
	}

	private _memoryBudget(configuredMaxResults: number) {
		return resolveMemoryPromptBudget({
			contextWindow: this.deps.getContextWindow(),
			configuredMaxResults,
		});
	}

	private _shouldQueryFileStoreFallback(budget: MemoryPromptBudget): boolean {
		// MEMORY.md/USER.md are already injected through the static file-store prompt on
		// normal windows, so querying them again would duplicate provider-visible memory.
		// In compact windows the static block can be omitted because it does not fit; only
		// then use the retrieval view to surface a few budget-pruned lines.
		if (!budget.enabled || !budget.compact) return false;
		return this._memoryManager.buildSystemPromptBlock(budget).trim().length === 0;
	}

	/**
	 * Observe-only local memory retrieval (see context/memory-retrieval.ts and
	 * context/okf-memory-provider.ts): default-on, but settings-gated. When disabled,
	 * never constructs built-in context-memory providers (no directory access under
	 * `_memoryOkfDir()` at all) and returns an empty report -- fully fail-closed. When enabled,
	 * queries local, read-only providers with the latest user message text (empty if there is
	 * none, e.g. a goal-continuation turn) under `DEFAULT_LOCAL_MEMORY_EGRESS_POLICY`.
	 * Retrieved items are only ever stored in the report; nothing here touches `messages`,
	 * the transcript, or the provider-visible prompt. Never throws into a live turn: any
	 * failure (including a provider search error) degrades to an empty report.
	 */
	async runMemoryRetrieval(messages: AgentMessage[]): Promise<MemoryRetrievalReport> {
		try {
			const settings = this.deps.getSettingsManager().getMemoryRetrievalSettings();
			if (!settings.enabled) {
				const report = emptyMemoryRetrievalReport(settings.maxResults);
				this._latestMemoryRetrievalReport = report;
				return report;
			}
			const query = latestUserMessageText(messages);
			const budget = this._memoryBudget(settings.maxResults);
			const currentWork = collectCurrentWorkMemory({ goalState: this.deps.getGoalState() });
			const longTermDecision = shouldQueryLongTermMemory({
				latestUserText: query,
				goalState: this.deps.getGoalState(),
				budget,
				currentWorkCandidateCount: currentWork.length,
			});
			const providers = this._shouldQueryFileStoreFallback(budget) ? [this._getFileStoreMemoryProvider()] : [];
			if (longTermDecision.shouldQuery) {
				providers.push(
					this._getMemoryOkfProvider(),
					...this._pendingContextMemoryProviders.filter((provider) => provider.capabilities.localOnly),
				);
				if (longTermDecision.shouldQueryExternal !== false) {
					providers.push(
						...this._pendingContextMemoryProviders.filter((provider) => !provider.capabilities.localOnly),
					);
				}
				const graph = this._getLocalGraphProvider();
				if (graph) providers.push(graph);
			}
			const maxResults = budget.enabled ? Math.min(settings.maxResults, budget.maxResults) : settings.maxResults;
			const report = await retrieveMemoryForContext(
				providers,
				{ query, maxResults },
				{
					createdAtTurn: this.deps.getTurnIndex(),
					maxResults,
					defaultLocalPolicy: DEFAULT_LOCAL_MEMORY_EGRESS_POLICY,
					defaultExternalPolicy: settings.allowExternalEgress
						? ENABLED_EXTERNAL_MEMORY_EGRESS_POLICY
						: DEFAULT_EXTERNAL_MEMORY_EGRESS_POLICY,
				},
			);
			this._latestMemoryRetrievalReport = report;
			return report;
		} catch {
			const report = emptyMemoryRetrievalReport(0);
			this._latestMemoryRetrievalReport = report;
			return report;
		}
	}

	/** Read-only inspection of the latest memory-retrieval report, for tests/debugging. */
	getMemoryRetrievalReport(): MemoryRetrievalReport {
		return this._latestMemoryRetrievalReport ?? emptyMemoryRetrievalReport(0);
	}

	private _candidateForContextItem(
		item: MemoryRetrievalReport["contextItems"][number],
	): MemoryTierCandidate | undefined {
		const summary = item.summary?.trim();
		if (!summary) return undefined;
		const ref = item.primaryRef?.type === "memory" ? item.primaryRef.ref : undefined;
		const sourceLabel =
			ref?.providerId === "pi-file-store" && ref.kind === "user_preference"
				? "rule:user"
				: `memory:${ref?.providerId ?? item.source}`;
		const tier = ref?.kind === "user_preference" ? "standing" : "long_term";
		return {
			id: item.id,
			tier,
			sourceLabel,
			summary,
			score: 0.5,
			evidenceRefs: item.evidenceRefs,
		};
	}

	private _memoryCandidates(report: MemoryRetrievalReport): MemoryTierCandidate[] {
		return [
			...collectCurrentWorkMemory({ goalState: this.deps.getGoalState() }),
			...report.contextItems.flatMap((item) => {
				const candidate = this._candidateForContextItem(item);
				return candidate === undefined ? [] : [candidate];
			}),
		];
	}

	/**
	 * Bounded prompt-surfacing for local memory evidence (see context/memory-tier-composer.ts):
	 * default-on, but gated on TWO settings (`enabled` AND `includeInPrompt`) plus at least one
	 * current-work, standing, or retrieved memory candidate -- the first
	 * two are belt-and-suspenders on top of the fact that `runMemoryRetrieval` already
	 * leaves `contextItems` empty whenever `enabled` is false, regardless of
	 * `includeInPrompt`. Reuses the `report` this pass's `runMemoryRetrieval` call already
	 * computed -- never re-queries the provider here.
	 *
	 * Appends exactly one ephemeral `custom`/"memory_evidence" message wrapped by
	 * `wrapUntrustedText` (the same nonce-fenced boundary + always-on system-prompt rule
	 * used for other untrusted content) to the END of `messages`. This is purely additive
	 * (never mutates an existing message) and purely transient: `messages` here is the
	 * array about to be sent to the provider, not `this.agent.state.messages` or anything
	 * persisted via `sessionManager` -- so the injected message can never reach the
	 * transcript, regardless of how many times this pass runs.
	 *
	 * Also records a `MemoryPromptInclusionReport` (context/memory-diagnostics.ts) at each
	 * branch below, for context_audit's diagnostic surface only -- this is pure bookkeeping
	 * alongside the existing branches, not a new branch/condition: the messages returned
	 * are unchanged by this recording.
	 */
	maybeAppendMemoryEvidenceBlock(messages: AgentMessage[], report: MemoryRetrievalReport): AgentMessage[] {
		try {
			const settings = this.deps.getSettingsManager().getMemoryRetrievalSettings();
			const candidates = this._memoryCandidates(report);
			const base = {
				enabled: settings.enabled,
				includeInPrompt: settings.includeInPrompt,
				selectedItemCount: candidates.length,
			};
			if (!settings.enabled) {
				this._latestMemoryPromptInclusionReport = {
					...base,
					status: "disabled",
					includedCount: 0,
					omittedCount: 0,
					blockChars: 0,
				};
				return messages;
			}
			if (!settings.includeInPrompt) {
				this._latestMemoryPromptInclusionReport = {
					...base,
					status: "include_disabled",
					includedCount: 0,
					omittedCount: 0,
					blockChars: 0,
				};
				return messages;
			}
			if (candidates.length === 0) {
				this._latestMemoryPromptInclusionReport = {
					...base,
					status: "no_results",
					includedCount: 0,
					omittedCount: 0,
					blockChars: 0,
				};
				return messages;
			}

			const budget = this._memoryBudget(settings.maxResults);
			const block = composeTieredMemoryPromptBlock(candidates, budget);
			if (!block.text) {
				this._latestMemoryPromptInclusionReport = {
					...base,
					status: "empty_block",
					includedCount: block.includedCount,
					omittedCount: block.omittedCount,
					blockChars: 0,
				};
				return messages;
			}

			const wrapped = wrapUntrustedText(block.text, "memory:tiered");
			const evidenceMessage: CustomMessage = {
				role: "custom",
				customType: "memory_evidence",
				content: [{ type: "text", text: wrapped }],
				display: false,
				timestamp: Date.now(),
			};
			this._latestMemoryPromptInclusionReport = {
				...base,
				status: "included",
				includedCount: block.includedCount,
				omittedCount: block.omittedCount,
				blockChars: wrapped.length,
				sourceLabel: "memory:tiered",
			};
			return [...messages, evidenceMessage];
		} catch {
			// `base` may not exist yet if the throw happened before it was computed (e.g.
			// settings access or `report.contextItems` itself threw), so this branch cannot
			// rely on it -- fall back to safe, fixed defaults rather than risk referencing
			// a partially-evaluated value.
			this._latestMemoryPromptInclusionReport = {
				enabled: false,
				includeInPrompt: false,
				selectedItemCount: 0,
				status: "failed",
				includedCount: 0,
				omittedCount: 0,
				blockChars: 0,
			};
			return messages;
		}
	}

	/** Read-only inspection of the latest memory-prompt-inclusion decision, for tests/debugging and context_audit. */
	getMemoryPromptInclusionReport(): MemoryPromptInclusionReport {
		return this._latestMemoryPromptInclusionReport ?? defaultMemoryPromptInclusionReport();
	}

	/**
	 * Combines the already-stored, no-arg latest reports (never re-queries the provider or
	 * touches the OKF directory) into the safe, allow-list-projected shape context_audit
	 * exposes. See context/memory-diagnostics.ts for why this projection is allow-list
	 * based rather than a spread-then-delete of the raw report.
	 */
	getMemoryAuditDiagnostics(): {
		retrieval: MemoryRetrievalDiagnostics;
		promptInclusion: MemoryPromptInclusionReport;
	} {
		const settings = this.deps.getSettingsManager().getMemoryRetrievalSettings();
		return {
			retrieval: sanitizeMemoryRetrievalReportForDiagnostics(this.getMemoryRetrievalReport(), settings),
			promptInclusion: this.getMemoryPromptInclusionReport(),
		};
	}

	/**
	 * Zero-I/O gate for cross-session recall (R3): skip trivial turns (short acks, slash commands) so
	 * recall only runs when it could plausibly help. The provider's similarity cutoff is the real
	 * filter — this just avoids the index query on turns that obviously don't warrant it.
	 */
	shouldAttemptRecall(text: string): boolean {
		if (!this.deps.getSettingsManager().getMemoryRetrievalSettings().enabled) return false;
		const t = text.trim();
		if (t.length < 12 || t.startsWith("/")) return false;
		const words = t.split(/\s+/).filter((w) => w.length >= 3);
		// R4 adaptive gate: if recall has rarely been used lately (enough samples to trust the signal),
		// raise the bar so we only recall on clearly substantial turns — and relax it again once recall
		// starts paying off. Never fully disabled, so the loop can recover.
		const recallRarelyUseful =
			this._effectivenessTracker.sampleCount >= 5 && this._effectivenessTracker.usefulLately() < 0.15;
		return words.length >= (recallRarelyUseful ? 6 : 3);
	}

	/** Legacy recall prefetch with the same hard-off and explicit external-egress policy as context retrieval. */
	async prefetchRecall(query: string): Promise<string> {
		const settings = this.deps.getSettingsManager().getMemoryRetrievalSettings();
		if (!settings.enabled) return "";
		return this._memoryManager.prefetch(query, {
			externalEgressPolicy: settings.allowExternalEgress
				? ENABLED_EXTERNAL_MEMORY_EGRESS_POLICY
				: DEFAULT_EXTERNAL_MEMORY_EGRESS_POLICY,
		});
	}

	/** Fresh bounded OKF snapshot for reflection's confront-before-write pass. */
	getFreshOkfMemoryForReflection(): string {
		try {
			const project = getDirectoryResourceProfileInfo(this.deps.getCwd(), this.deps.getAgentDir());
			const entries = loadOkfMemoryBundle({
				rootDir: this._memoryOkfDir(),
				projectId: project.hash,
				projectRoot: project.root,
				maxDocuments: 64,
			}).entries;
			const snapshot = entries
				.map(({ relativePath, parsed }) => {
					const item = parsed.item;
					return item === undefined
						? ""
						: `[OKF ${relativePath}] ${item.title ?? "Untitled"}: ${item.summary}\n${item.content ?? ""}`;
				})
				.filter((entry) => entry.length > 0)
				.join("\n\n")
				.slice(0, 12_000);
			return snapshot.length > 0 ? wrapUntrustedText(snapshot, "memory:reflection-okf") : "";
		} catch {
			return "";
		}
	}

	/** Bounded, read-only memory view for an explicitly authorized delegated worker. */
	async readMemoryForLane(query: string): Promise<string> {
		const settings = this.deps.getSettingsManager().getMemoryRetrievalSettings();
		if (!settings.enabled) return "Memory retrieval is disabled by policy.";
		const budget = this._memoryBudget(settings.maxResults);
		const staticBlock = this._memoryManager
			.buildSystemPromptBlockFresh(budget)
			.replace(FILE_STORE_MEMORY_SYSTEM_NOTE, "[Read-only snapshot for a delegated worker.]");
		const [recalled, okfReport] = await Promise.all([
			this.prefetchRecall(query),
			retrieveMemoryForContext(
				[this._getMemoryOkfProvider()],
				{ query, maxResults: Math.min(3, settings.maxResults) },
				{
					createdAtTurn: this.deps.getTurnIndex(),
					maxResults: Math.min(3, settings.maxResults),
					defaultLocalPolicy: DEFAULT_LOCAL_MEMORY_EGRESS_POLICY,
				},
			),
		]);
		const okf = okfReport.results
			.map(({ item }) => `[OKF ${item.title ?? item.id}] ${item.summary}\n${item.content ?? ""}`)
			.join("\n\n");
		const combined = [staticBlock, okf, recalled]
			.filter((part) => part.trim().length > 0)
			.join("\n\n")
			.slice(0, 8000);
		return combined.length > 0
			? wrapUntrustedText(combined, "worker-memory")
			: "No relevant standing memory was found.";
	}

	/** Parent reflection's only structured-memory mutation port. Workers never receive this capability. */
	async applyStructuredReflectionWrite(
		write: StructuredReflectionWrite,
		signal?: AbortSignal,
	): Promise<StructuredReflectionApplyResult> {
		if (this.deps.isChildSession() || this._fileStoreWriter === undefined) {
			return { applied: false, created: false, error: "Structured memory writes are unavailable." };
		}
		return this._fileStoreWriter.applyStructuredReflectionWrite(write, signal);
	}

	/** Parent-owned inverse for an audited structured reflection write. */
	async rollbackStructuredReflectionWrite(
		rollback: StructuredReflectionRollback,
		signal?: AbortSignal,
	): Promise<boolean> {
		if (this.deps.isChildSession() || this._fileStoreWriter === undefined) return false;
		return this._fileStoreWriter.rollbackStructuredReflectionWrite(rollback, signal);
	}

	/** R4: score whether the agent actually used an injected recall page, so the recall gate can adapt. */
	recordRecallOutcome(recallText: string, queryText: string, responseText: string): void {
		this._effectivenessTracker.recordRecallOutcome(recallText, queryText, responseText);
	}

	/**
	 * (Re)build the memory subsystem: a fresh MemoryManager (reload-safe), register the bundled
	 * file-store + any extension-contributed providers, initialize, then surface the memory tools and
	 * the frozen system-prompt block. Best-effort: never throws into the session lifecycle.
	 */
	async initialize(): Promise<void> {
		try {
			this._fileStoreWriter = undefined;
			await this._lifecycleTail;
			// Release the previous generation's providers (locks/handles) before recreating, so a
			// reload does not orphan the old MemoryManager. No-op on first init / for file-store.
			await this._memoryManager.shutdownAll().catch(() => {});
			this._localGraphProvider = undefined;
			this._localGraphResolved = false;
			const manager = new MemoryManager();
			const fileStoreWriter = new FileStoreProvider({
				onDurableMemoryChanged: () => {
					// OKF providers cache one bounded directory load. A USER.md overflow can create or
					// update a shard during this session, so discard only that read cache generation.
					this._memoryOkfProvider = undefined;
				},
			});
			manager.registerProvider(fileStoreWriter);
			// Bundled read-only cross-session recall (R3): indexes past-session transcripts and answers
			// prefetch() with a <memory_context> page. Never writes.
			manager.registerProvider(new TranscriptRecallProvider());
			for (const provider of this._pendingMemoryProviders) {
				try {
					manager.registerProvider(provider);
				} catch {
					// Duplicate name or reserved-tool collision — skip this provider, keep the rest.
				}
			}
			this._memoryManager = manager;
			await manager.initializeAll(this.deps.getSessionId(), {
				agentDir: this.deps.getAgentDir(),
				cwd: this.deps.getCwd(),
				isChildSession: this.deps.isChildSession(),
			});
			this._fileStoreWriter = manager.getToolDefinitions().some((tool) => tool.name === "memory")
				? fileStoreWriter
				: undefined;
			// Surface memory tools + the frozen memory block now that providers are initialized.
			// refreshToolRegistry() ends in setActiveToolsByName(), which rebuilds AND assigns the
			// system prompt (including the memory block), so no explicit _rebuildSystemPrompt is needed.
			this.deps.refreshToolRegistry();
		} catch (error) {
			console.error("Memory subsystem init failed:", error instanceof Error ? error.message : String(error));
		}
	}

	/** Register a memory provider contributed by an extension; applied on the next memory (re)init. */
	registerMemoryProvider(provider: MemoryProvider): void {
		if (!this._pendingMemoryProviders.some((p) => p.name === provider.name)) {
			this._pendingMemoryProviders.push(provider);
		}
	}

	/** Register a retrieval-style context memory provider contributed by an extension. */
	registerContextMemoryProvider(provider: ContextMemoryProvider): void {
		if (!this._pendingContextMemoryProviders.some((p) => p.id === provider.id)) {
			this._pendingContextMemoryProviders.push(provider);
		}
	}

	createReloadSnapshot(): MemoryControllerReloadSnapshot {
		return {
			pendingMemoryProviders: [...this._pendingMemoryProviders],
			pendingContextMemoryProviders: [...this._pendingContextMemoryProviders],
			memoryOkfProvider: this._memoryOkfProvider,
			fileStoreMemoryProvider: this._fileStoreMemoryProvider,
		};
	}

	restoreReloadSnapshot(snapshot: MemoryControllerReloadSnapshot): void {
		this._pendingMemoryProviders = [...snapshot.pendingMemoryProviders];
		this._pendingContextMemoryProviders = [...snapshot.pendingContextMemoryProviders];
		this._memoryOkfProvider = snapshot.memoryOkfProvider;
		this._fileStoreMemoryProvider = snapshot.fileStoreMemoryProvider;
	}

	/** Reload starts memory providers fresh; loaded extensions re-register before the next `initialize()`. */
	clearPendingProviders(): void {
		this._pendingMemoryProviders = [];
		this._pendingContextMemoryProviders = [];
		this._memoryOkfProvider = undefined;
		this._fileStoreMemoryProvider = undefined;
	}
}
